import React, { useCallback, useEffect, useState } from "react";
import { Alert, Box, Button, CircularProgress, Table, TableBody, TableCell, TableHead, TableRow, TablePagination, TextField, Typography } from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import RefreshIcon from "@mui/icons-material/Refresh";
import { getDiscretionaryAllocations, saveDiscretionaryAllocations } from "../../api/financeApi";
import { getApiErrorMessage } from "../../utils/concurrency";

const money = (value) => new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(value);

export default function DiscretionaryAllocationsEditor({ propertyId, budgetId, sectionId, revision, onMessage, onSaved }) {
  const [data, setData] = useState(null);
  const [edits, setEdits] = useState({});
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const dirty = Object.keys(edits).length > 0;
  const load = useCallback(async () => {
    setBusy(true);
    setError("");
    try {
      const res = await getDiscretionaryAllocations(propertyId, budgetId, sectionId, { page: page + 1, page_size: pageSize });
      setData(res.data);
      setEdits({});
    } catch (e) {
      setError(getApiErrorMessage(e, "Failed to load allocations."));
    } finally { setBusy(false); }
  }, [propertyId, budgetId, sectionId, page, pageSize]);
  useEffect(() => { load(); }, [load, revision]);
  const total = Number(data?.section_total || 0);
  const rows = (data?.results || []).map((row) => ({ ...row, ...edits[row.unit_id] }));
  const allocated = Number(data?.allocated_amount || 0) + rows.reduce((sum, row, i) => sum + Number(row.amount || 0) - Number(data.results[i].amount), 0);
  const totalPct = total ? allocated / total * 100 : 0;
  const balanced = Math.abs(allocated - total) < 0.005;
  const edit = (row, field, value) => {
    const next = { ...row, [field]: value };
    if (field === "percentage") {
      next.amount = value === "" ? "" : (Number(value) * total / 100).toFixed(2);
      next.basis = "percentage";
    } else if (field === "amount") {
      next.percentage = value === "" ? "" : (total ? Number(value) / total * 100 : 0).toFixed(4);
      next.basis = "amount";
    }
    setEdits((prev) => ({ ...prev, [row.unit_id]: next }));
  };
  const save = async () => {
    setBusy(true);
    setError("");
    try {
      const res = await saveDiscretionaryAllocations(propertyId, budgetId, sectionId, {
        row_version: data.row_version,
        budget_row_version: data.budget_row_version,
        property_row_version: data.property_row_version,
        allocations: Object.values(edits).map((row) => ({ unit_id: row.unit_id, [row.basis || "amount"]: row[row.basis || "amount"], reason: row.reason })),
      }, { page: page + 1, page_size: pageSize });
      setData(res.data);
      setEdits({});
      onSaved?.(res.data);
      onMessage?.("Discretionary allocations saved.");
    } catch (e) { setError(getApiErrorMessage(e, "Failed to save allocations.")); }
    finally { setBusy(false); }
  };
  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle1">Discretionary allocations</Typography>
      <Typography variant="body2" sx={{ mb: 1 }}>
        Edit a percentage or gross amount to see the other update. Cash amounts stay fixed when the budget changes; percentages recalculate. Add an optional reason, such as “two spaces”.
      </Typography>
      {error && <Alert severity="error">{error}</Alert>}
      {data && <Alert severity={balanced ? "success" : "warning"} sx={{ my: 1 }}>
        Section total {money(total)} · Allocated {totalPct.toFixed(4)}% ({money(allocated)}) · Remaining {money(total - allocated)}.
        {!balanced && " Complete the split to 100% before generating invoices or applying charges."}
      </Alert>}
      <Box sx={{ overflowX: "auto" }}>
        <Table size="small" aria-label="Discretionary allocations">
          <TableHead><TableRow><TableCell>Unit</TableCell><TableCell>Percentage</TableCell><TableCell>Amount GBP</TableCell><TableCell>Reason</TableCell></TableRow></TableHead>
          <TableBody>{rows.map((row) => (
            <TableRow key={row.unit_id}>
              <TableCell>{row.unit_name}</TableCell>
              <TableCell><TextField size="small" type="number" value={row.percentage} disabled={busy || data.read_only || total <= 0} onChange={(e) => edit(row, "percentage", e.target.value)} inputProps={{ min: 0, max: 100, step: 0.0001, "aria-label": `${row.unit_name} percentage` }} /></TableCell>
              <TableCell><TextField size="small" type="number" value={row.amount} disabled={busy || data.read_only} onChange={(e) => edit(row, "amount", e.target.value)} inputProps={{ min: 0, step: 0.01, "aria-label": `${row.unit_name} amount` }} /></TableCell>
              <TableCell><TextField size="small" value={row.reason} disabled={busy || data.read_only} onChange={(e) => edit(row, "reason", e.target.value)} inputProps={{ maxLength: 500, "aria-label": `${row.unit_name} reason` }} /></TableCell>
            </TableRow>
          ))}</TableBody>
        </Table>
      </Box>
      <TablePagination component="div" count={data?.total || 0} page={page} rowsPerPage={pageSize} rowsPerPageOptions={[10, 25, 50]} onPageChange={(_, value) => setPage(value)} onRowsPerPageChange={(e) => { setPageSize(Number(e.target.value)); setPage(0); }} disabled={busy || dirty} slotProps={{ actions: { nextButton: { disabled: busy || dirty || (page + 1) * pageSize >= (data?.total || 0) }, previousButton: { disabled: busy || dirty || page === 0 } }, select: { disabled: busy || dirty } }} />
      {dirty && <Typography variant="caption">Save or reload your changes before changing pages.</Typography>}
      <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
        <Button variant="contained" startIcon={busy ? <CircularProgress size={16} /> : <SaveIcon />} onClick={save} disabled={busy || !dirty || data?.read_only}>Save allocations</Button>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={load} disabled={busy}>Reload</Button>
      </Box>
    </Box>
  );
}
